import type { Probe, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * Plugin and MIME-type enumeration, plus a few derived browser checks.
 *
 * navigator.plugins is deprecated and always returns a frozen, five-item
 * "PDF Viewer / Chrome PDF Viewer / ..." list in modern Chromium, but that list
 * is NOT identical across engines and browsers (Firefox and Safari differ), and
 * the *count* plus each plugin's filename list is still a cheap, stable
 * differentiator. More importantly, an anti-detect browser that rewrites
 * navigator.plugins to empty — a common over-correction — is itself detectable:
 * real Chrome reports exactly five entries.
 */
export const pluginsProbe: Probe = {
  id: 'plugins',
  label: 'Plugins & MIME types',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'navigator.plugins / mimeTypes, and common property-presence checks',
  budgetMs: 1000,

  async run(): Promise<ProbeResult> {
    const pluginList = Array.from(navigator.plugins ?? []);
    const mimeList = Array.from(navigator.mimeTypes ?? []);

    const plugins = pluginList.map((p) => ({
      name: p.name,
      filename: p.filename,
      description: p.description,
      mimeTypeCount: Array.from(p).length,
    }));

    const mimeTypes = mimeList.map((m) => ({
      type: m.type,
      suffixes: m.suffixes,
      description: m.description,
    }));

    // Property-presence checks. Each is a boolean; a modern browser has a
    // specific set true, and automation/stealth tooling frequently blanks them.
    const presence = checkPresence();

    return {
      signals: {
        pluginCount: ok(pluginList.length, 'client'),
        plugins: ok(plugins, 'client'),
        mimeTypeCount: ok(mimeList.length, 'client'),
        mimeTypes: ok(mimeTypes, 'client'),
        // Chrome's frozen five-entry list; a count of 0 in a Chrome UA is a
        // classic over-spoofed tell.
        chromePdfPluginSet: ok(isChromePdfPluginSet(plugins), 'derived'),
        propertyPresence: ok(presence, 'client'),
        presenceBitmask: ok(
          Object.values(presence)
            .map((v) => (v ? '1' : '0'))
            .join(''),
          'derived',
        ),
      },
      parts: {
        browserFeatures: {
          pluginCount: pluginList.length,
          mimeTypeCount: mimeList.length,
          // Hash names only; descriptions vary by locale and version.
          pluginNames: plugins.map((p) => p.name).sort(),
        },
      },
    };
  },
};

function isChromePdfPluginSet(plugins: Array<{ name: string }>): boolean {
  if (plugins.length !== 5) return false;
  const names = plugins.map((p) => p.name);
  return names.includes('PDF Viewer') && names.includes('Chrome PDF Viewer');
}

function checkPresence(): Record<string, boolean> {
  const w = window as unknown as Record<string, unknown>;
  const n = navigator as unknown as Record<string, unknown>;
  return {
    applePaySupport: 'ApplePaySession' in w,
    opera: typeof w.opera !== 'undefined' || typeof w.onoperadetachedviewchange !== 'undefined',
    // Safari-specific constructor.
    safari: 'safari' in w,
    // moz-prefixed leftovers (old Firefox).
    mozGetUserMedia: 'mozGetUserMedia' in n,
    mozRequestAnimationFrame: 'mozRequestAnimationFrame' in w,
    // Chromium-only.
    showModalDialog: 'showModalDialog' in w,
    documentPictureInPicture: 'documentPictureInPicture' in w,
    eyeDropper: 'EyeDropper' in w,
    editContext: 'EditContext' in w,
    fencedFrame: 'HTMLFencedFrameElement' in w,
    sanitizer: 'Sanitizer' in w,
    otpCredential: 'OTPCredential' in w,
    // Widely supported now, but their absence is still a differentiator.
    barCodeDetector: 'BarcodeDetector' in w,
    pressureObserver: 'PressureObserver' in w,
    audioSession: 'audioSession' in n,
    selectAudioOutput: 'selectAudioOutput' in n,
    paymentRequestUpdateEvent: 'PaymentRequestUpdateEvent' in w,
    devicePosture: 'devicePosture' in n,
    // Math.sumPrecise is a Firefox-only proposal function; its presence is a
    // cheap engine differentiator.
    sumPrecise: 'sumPrecise' in Math,
  };
}

registerProbe(pluginsProbe);
